import { ExtendedCreateProjectData, ProjectFormState } from '@/types';
import { CheckCircle, ChevronLeft, ChevronRight, Circle } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Step Components
import DirectoryStep from './project-form-steps/DirectoryStep';
import DrawingsStep from './project-form-steps/DrawingsStep';
import ProjectDetailsStep from './project-form-steps/ProjectDetailsStep';
import ScheduleStep from './project-form-steps/ScheduleStep';
import SpecificationsStep from './project-form-steps/SpecificationsStep';
import TeamStep from './project-form-steps/TeamStep';
import ToolsStep from './project-form-steps/ToolsStep';

interface MultiStepProjectFormProps {
  onSubmit: (data: ExtendedCreateProjectData) => void;
  onCancel: () => void;
  initialData?: Partial<ExtendedCreateProjectData>;
  isLoading?: boolean;
}

const MultiStepProjectForm: React.FC<MultiStepProjectFormProps> = ({
  onSubmit,
  onCancel,
  initialData = {},
  isLoading = false,
}) => {
  const { t } = useTranslation();

  // Memoized steps configuration
  const steps = useMemo(
    () => [
      {
        id: 1,
        name: t('projects.creation.steps.projectDetails'),
        description: t('projects.creation.stepDescriptions.projectDetails'),
      },
      {
        id: 2,
        name: t('projects.creation.steps.team'),
        description: t('projects.creation.stepDescriptions.team'),
      },
      {
        id: 3,
        name: t('projects.creation.steps.tools'),
        description: t('projects.creation.stepDescriptions.tools'),
      },
      {
        id: 4,
        name: t('projects.creation.steps.directory'),
        description: t('projects.creation.stepDescriptions.directory'),
      },
      {
        id: 5,
        name: t('projects.creation.steps.drawings'),
        description: t('projects.creation.stepDescriptions.drawings'),
      },
      {
        id: 6,
        name: t('projects.creation.steps.specifications'),
        description: t('projects.creation.stepDescriptions.specifications'),
      },
      {
        id: 7,
        name: t('projects.creation.steps.schedule'),
        description: t('projects.creation.stepDescriptions.schedule'),
      },
    ],
    [t]
  );

  // Initialize form state with better defaults
  const [formState, setFormState] = useState<ProjectFormState>(() => ({
    currentStep: 1,
    totalSteps: steps.length,
    data: {
      name: typeof initialData.name === 'string' ? initialData.name : '',
      description: typeof initialData.description === 'string' ? initialData.description : '',
      address: typeof initialData.address === 'string' ? initialData.address : '',
      startDate: initialData.startDate instanceof Date ? initialData.startDate : new Date(),
      endDate:
        initialData.endDate instanceof Date
          ? initialData.endDate
          : (() => {
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              return tomorrow;
            })(),
      budget: typeof initialData.budget === 'number' ? initialData.budget : 0,
      teamMembers: Array.isArray(initialData.teamMembers) ? initialData.teamMembers : [],
      tools: Array.isArray(initialData.tools) ? initialData.tools : [],
      directories: Array.isArray(initialData.directories) ? initialData.directories : [],
      customFields:
        initialData.customFields && typeof initialData.customFields === 'object'
          ? initialData.customFields
          : {},
      drawings: Array.isArray(initialData.drawings) ? initialData.drawings : [],
      specifications: Array.isArray(initialData.specifications) ? initialData.specifications : [],
      schedules: Array.isArray(initialData.schedules) ? initialData.schedules : [],
      location: {
        country: initialData.location?.country || '',
        state: initialData.location?.state || '',
        city: initialData.location?.city || '',
        zipCode: initialData.location?.zipCode || '',
        address: initialData.location?.address || initialData.address || '',
        timezone: initialData.location?.timezone || '',
        phone: initialData.location?.phone || '',
        coordinates: initialData.location?.coordinates || undefined,
      },
      advanced: {
        region: initialData.advanced?.region || '',
        language: initialData.advanced?.language || 'en',
        currency: initialData.advanced?.currency || 'USD',
        isTestProject: initialData.advanced?.isTestProject || false,
      },
      detailedTeamMembers: Array.isArray(initialData.detailedTeamMembers)
        ? initialData.detailedTeamMembers
        : [],
    },
    isValid: {},
  }));

  // Optimized form data update with useCallback
  const updateFormData = useCallback((stepData: Partial<ExtendedCreateProjectData>) => {
    setFormState(prev => ({
      ...prev,
      data: {
        ...prev.data,
        ...stepData,
      },
    }));
  }, []);

  // Optimized step validation update
  const setStepValid = useCallback((step: number, isValid: boolean) => {
    setFormState(prev => ({
      ...prev,
      isValid: {
        ...prev.isValid,
        [step]: isValid,
      },
    }));
  }, []);

  // Navigation functions
  const goToStep = useCallback(
    (step: number) => {
      if (step >= 1 && step <= steps.length) {
        setFormState(prev => ({
          ...prev,
          currentStep: step,
        }));
      }
    },
    [steps.length]
  );

  const goNext = useCallback(() => {
    if (formState.currentStep < steps.length) {
      setFormState(prev => ({
        ...prev,
        currentStep: prev.currentStep + 1,
      }));
    }
  }, [formState.currentStep, steps.length]);

  const goPrevious = useCallback(() => {
    if (formState.currentStep > 1) {
      setFormState(prev => ({
        ...prev,
        currentStep: prev.currentStep - 1,
      }));
    }
  }, [formState.currentStep]);

  const handleSubmit = useCallback(() => {
    onSubmit(formState.data as ExtendedCreateProjectData);
  }, [formState.data, onSubmit]);

  // Check if current step is valid
  const isCurrentStepValid = formState.isValid[formState.currentStep] ?? false;
  const isLastStep = formState.currentStep === steps.length;
  const isFirstStep = formState.currentStep === 1;

  // Get step status for progress indicator
  const getStepStatus = useCallback(
    (stepId: number) => {
      if (stepId < formState.currentStep) return 'completed';
      if (stepId === formState.currentStep) return 'current';
      return 'upcoming';
    },
    [formState.currentStep]
  );

  // Render the current step component
  const renderCurrentStep = () => {
    const commonProps = {
      data: formState.data,
      updateData: updateFormData,
      setValid: (isValid: boolean) => setStepValid(formState.currentStep, isValid),
    };

    switch (formState.currentStep) {
      case 1:
        return <ProjectDetailsStep {...commonProps} />;
      case 2:
        return <TeamStep {...commonProps} />;
      case 3:
        return <ToolsStep {...commonProps} />;
      case 4:
        return <DirectoryStep {...commonProps} />;
      case 5:
        return <DrawingsStep {...commonProps} />;
      case 6:
        return <SpecificationsStep {...commonProps} />;
      case 7:
        return <ScheduleStep {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full">
      {/* Clean Progress Stepper */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-600">
                Step {formState.currentStep} of {steps.length}
              </div>
              <h2 className="text-lg font-semibold text-gray-900 mt-1">
                {steps[formState.currentStep - 1]?.name}
              </h2>
            </div>
            <div className="text-sm text-gray-500">
              {Math.round((formState.currentStep / steps.length) * 100)}% Complete
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-red-500 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(formState.currentStep / steps.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Horizontal Step Indicators */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between relative">
            {/* Progress Line */}
            <div className="absolute top-4 left-4 right-4 h-0.5 bg-gray-200">
              <div
                className="h-full bg-red-500 transition-all duration-500 ease-out"
                style={{ width: `${((formState.currentStep - 1) / (steps.length - 1)) * 100}%` }}
              />
            </div>

            {steps.map(step => {
              const status = getStepStatus(step.id);
              const isClickable = step.id <= formState.currentStep || formState.isValid[step.id];

              return (
                <div key={step.id} className="flex flex-col items-center relative z-10">
                  <button
                    onClick={() => isClickable && goToStep(step.id)}
                    disabled={!isClickable}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-200 border-2 ${
                      status === 'completed'
                        ? 'bg-red-500 border-red-500 text-white hover:bg-red-600'
                        : status === 'current'
                          ? 'bg-white border-red-500 text-red-500 shadow-sm'
                          : 'bg-white border-gray-300 text-gray-400'
                    } ${isClickable ? 'hover:scale-110 cursor-pointer' : 'cursor-not-allowed'}`}
                    title={step.name}
                  >
                    {status === 'completed' ? <CheckCircle className="w-4 h-4" /> : step.id}
                  </button>
                  <span
                    className={`mt-2 text-xs font-medium text-center max-w-20 line-clamp-2 ${
                      status === 'current'
                        ? 'text-red-500'
                        : status === 'completed'
                          ? 'text-gray-900'
                          : 'text-gray-500'
                    }`}
                  >
                    {step.name}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Current Step Content */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-200 mb-6">
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <span className="text-red-500 font-semibold text-sm">{formState.currentStep}</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {steps[formState.currentStep - 1]?.name}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {steps[formState.currentStep - 1]?.description}
              </p>
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="p-6">
          <div className="min-h-[400px]">{renderCurrentStep()}</div>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              {t('projects.creation.navigation.cancel')}
            </button>

            <div className="flex items-center space-x-3">
              {!isFirstStep && (
                <button
                  onClick={goPrevious}
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {t('projects.creation.navigation.previous')}
                </button>
              )}

              {isLastStep ? (
                <button
                  onClick={handleSubmit}
                  disabled={!isCurrentStepValid || isLoading}
                  className="inline-flex items-center px-6 py-2 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {isLoading ? (
                    <>
                      <Circle className="w-4 h-4 mr-2 animate-spin" />
                      {initialData.id
                        ? t('projects.creation.navigation.updating')
                        : t('projects.creation.navigation.saving')}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      {initialData.id
                        ? t('projects.creation.navigation.updateProject')
                        : t('projects.creation.navigation.createProject')}
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={goNext}
                  disabled={!isCurrentStepValid || isLoading}
                  className="inline-flex items-center px-6 py-2 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {t('projects.creation.navigation.next')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiStepProjectForm;
